// ===========================================
// Google Calendar API Functions
// ===========================================
// API calls for Google Calendar integration endpoints
// OAuth, calendar selection, sync control

import { apiClient } from '../services/api';

// ===========================================
// ENUMS & TYPES
// ===========================================

export type SyncStatus = 'PENDING' | 'SYNCING' | 'SYNCED' | 'ERROR';

// ===========================================
// AUTH TYPES
// ===========================================

export interface GoogleCalendarAuthStatus {
  isConnected: boolean;
  calendarId: string | null;
  calendarName: string | null;
  syncEnabled: boolean;
  lastSyncAt: string | null;
  hasErrors: boolean;
  syncStats: {
    total: number;
    synced: number;
    pending: number;
    errors: number;
  } | null;
}

export interface AuthUrlResponse {
  authUrl: string;
}

// ===========================================
// CALENDAR TYPES
// ===========================================

/** Google Calendar from user's account */
export interface GoogleCalendar {
  id: string;
  summary: string;
  description?: string;
  primary?: boolean;
  accessRole: string;
  backgroundColor?: string;
}

// ===========================================
// SYNC TYPES
// ===========================================

export interface SyncResult {
  synced: number;
  errors: number;
  message: string;
}

export interface SyncError {
  lessonId: string;
  lessonName: string;
  errorMessage: string;
}

export interface TriggerSyncInput {
  lessonIds?: string[];
  fullSync?: boolean;
}

// ===========================================
// SETTINGS TYPES
// ===========================================

export interface UpdateSettingsInput {
  calendarId?: string;
  syncEnabled?: boolean;
}

export interface SelectCalendarInput {
  calendarId: string;
  calendarName: string;
}

// ===========================================
// PER-STUDENT MAPPING TYPES
// ===========================================

/** A child + which Google calendar their lessons sync to (or the default). */
export interface StudentCalendarMapping {
  studentId: string;
  firstName: string;
  lastName: string;
  calendarId: string | null;
  calendarName: string | null;
  usingDefault: boolean;
}

/** Result of setting/clearing a child's mapping (includes the event-move outcome). */
export interface StudentMappingResult {
  mapping?: StudentCalendarMapping;
  studentId?: string;
  usingDefault?: boolean;
  moved: number;
  errors: number;
}

// ===========================================
// API RESPONSE WRAPPERS
// ===========================================

interface ApiResponse<T> {
  status: string;
  data: T;
  message?: string;
}

// ===========================================
// GOOGLE CALENDAR API
// ===========================================

export const googleCalendarApi = {
  // ===========================================
  // OAUTH AUTHENTICATION
  // ===========================================

  /**
   * Get OAuth authorization URL to connect Google Calendar
   * Teacher or Admin only
   */
  getAuthUrl: (): Promise<AuthUrlResponse> =>
    apiClient
      .get<ApiResponse<AuthUrlResponse>>('/integrations/google-calendar/auth-url')
      .then((res) => res.data),

  /**
   * Check if Google Calendar is connected for the user
   * Teacher or Admin only
   */
  getStatus: (): Promise<GoogleCalendarAuthStatus> =>
    apiClient
      .get<ApiResponse<GoogleCalendarAuthStatus>>('/integrations/google-calendar/status')
      .then((res) => res.data),

  /**
   * Disconnect Google Calendar integration.
   * @param removeEvents true = delete the synced lessons from Google Calendar;
   *   false = leave them in place (they stop updating). Defaults to true.
   * Available to all authenticated users (parents sync per-child calendars too).
   */
  disconnect: (removeEvents: boolean = true): Promise<void> =>
    apiClient
      .delete('/integrations/google-calendar', { data: { removeEvents } })
      .then(() => undefined),

  // ===========================================
  // CALENDAR MANAGEMENT
  // ===========================================

  /**
   * List available calendars from user's Google account
   * Teacher or Admin only
   */
  listCalendars: (): Promise<GoogleCalendar[]> =>
    apiClient
      .get<ApiResponse<{ calendars: GoogleCalendar[] }>>('/integrations/google-calendar/calendars')
      .then((res) => res.data.calendars),

  /**
   * Select which calendar to sync events to
   * Teacher or Admin only
   */
  selectCalendar: (input: SelectCalendarInput): Promise<{ calendarId: string; calendarName: string }> =>
    apiClient
      .put<ApiResponse<{ calendarId: string; calendarName: string }>>(
        '/integrations/google-calendar/select',
        input
      )
      .then((res) => res.data),

  /**
   * Update calendar integration settings
   * Teacher or Admin only
   */
  updateSettings: (input: UpdateSettingsInput): Promise<{
    calendarId: string | null;
    calendarName: string | null;
    syncEnabled: boolean;
  }> =>
    apiClient
      .put<ApiResponse<{ calendarId: string | null; calendarName: string | null; syncEnabled: boolean }>>(
        '/integrations/google-calendar/settings',
        input
      )
      .then((res) => res.data),

  // ===========================================
  // SYNC MANAGEMENT
  // ===========================================

  /**
   * Trigger manual sync of lessons to calendar
   * Teacher or Admin only
   */
  triggerSync: (input?: TriggerSyncInput): Promise<SyncResult> =>
    apiClient
      .post<ApiResponse<SyncResult>>('/integrations/google-calendar/sync', input || {})
      .then((res) => res.data),

  /**
   * Get lessons with sync errors
   * Teacher or Admin only
   */
  getSyncErrors: (): Promise<SyncError[]> =>
    apiClient
      .get<ApiResponse<{ errors: SyncError[] }>>('/integrations/google-calendar/sync/errors')
      .then((res) => res.data.errors),

  // ===========================================
  // PER-STUDENT CALENDAR MAPPING (Parents)
  // ===========================================

  /**
   * List the caller's children + their current per-child calendar (or default).
   * Returns [] for non-parents / parents with no family.
   */
  getStudentMappings: (): Promise<StudentCalendarMapping[]> =>
    apiClient
      .get<ApiResponse<{ children: StudentCalendarMapping[] }>>(
        '/integrations/google-calendar/student-mappings'
      )
      .then((res) => res.data.children),

  /**
   * Set / change a child's calendar (moves that child's events to it).
   */
  setStudentMapping: (
    studentId: string,
    input: SelectCalendarInput
  ): Promise<StudentMappingResult> =>
    apiClient
      .put<ApiResponse<StudentMappingResult>>(
        `/integrations/google-calendar/student-mappings/${studentId}`,
        input
      )
      .then((res) => res.data),

  /**
   * Clear a child's mapping (reverts to default, moves events back).
   */
  clearStudentMapping: (studentId: string): Promise<StudentMappingResult> =>
    apiClient
      .delete<ApiResponse<StudentMappingResult>>(
        `/integrations/google-calendar/student-mappings/${studentId}`
      )
      .then((res) => res.data),
};

// ===========================================
// HELPER FUNCTIONS
// ===========================================

/**
 * Get sync status color for chip/badge
 */
export const getSyncStatusColor = (
  status: SyncStatus
): 'default' | 'primary' | 'success' | 'error' => {
  switch (status) {
    case 'SYNCED':
      return 'success';
    case 'SYNCING':
      return 'primary';
    case 'ERROR':
      return 'error';
    case 'PENDING':
    default:
      return 'default';
  }
};

/**
 * Get sync status label for display
 */
export const getSyncStatusLabel = (status: SyncStatus): string => {
  switch (status) {
    case 'SYNCED':
      return 'Synced';
    case 'SYNCING':
      return 'Syncing...';
    case 'ERROR':
      return 'Error';
    case 'PENDING':
      return 'Pending';
    default:
      return status;
  }
};

/**
 * Format timestamp for display
 */
export const formatSyncTime = (timestamp: string | null): string => {
  if (!timestamp) return 'Never';

  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString();
};
